// ResizeHandle — vertical divider between SidePanel and MainPanel.
//
// Drag the bar to resize the side panel; click the centered chevron to
// collapse / expand. State lives in the ui slice (per-side, session-only).
// Pure pointer events — no third-party resize library.

import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { t } from '../../../config/strings';
import { cn } from '../../../lib/cn';
import { useStore } from '../../../store';
import type { Side } from '../../../store/predictions';

export interface ResizeHandleProps {
  side: Side;
  sidePanelOnLeft: boolean;
}

// If pointer moved less than this between down and up, treat as a click on the
// chevron (collapse toggle) rather than a drag.
const CLICK_THRESHOLD_PX = 3;

export function ResizeHandle({ side, sidePanelOnLeft }: ResizeHandleProps): React.ReactElement {
  const width = useStore((s) => s.sidePanelWidth[side]);
  const collapsed = useStore((s) => s.sidePanelCollapsed[side]);
  const setSidePanelWidth = useStore((s) => s.setSidePanelWidth);
  const toggleSidePanelCollapsed = useStore((s) => s.toggleSidePanelCollapsed);

  const dragRef = React.useRef<{
    startX: number;
    startWidth: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Only the primary button initiates a drag; let other buttons fall through.
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startWidth: collapsed ? 0 : width,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = e.clientX - drag.startX;
    if (Math.abs(delta) < CLICK_THRESHOLD_PX && !drag.moved) return;
    drag.moved = true;
    // For left-view (handle to the right of side panel), pulling right grows
    // the panel. For right-view (handle to the left of side panel), pulling
    // left grows the panel.
    const signedDelta = sidePanelOnLeft ? delta : -delta;
    const nextWidth = drag.startWidth + signedDelta;
    // Auto-expand if user starts dragging while collapsed.
    if (collapsed) {
      toggleSidePanelCollapsed(side);
    }
    setSidePanelWidth(side, nextWidth);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
  };

  const handleClick = (): void => {
    // If a drag just happened the click is suppressed (moved flag was set and
    // dragRef cleared in endDrag, but React fires click after pointerup —
    // dragRef is already null, so we use a small dataset flag instead).
    // Simpler: check the last drag's moved flag via a ref captured in pointerup.
    // We emulate this by storing on the element with a data attribute.
    toggleSidePanelCollapsed(side);
  };

  // Track whether the most recent pointer interaction was a drag, to suppress
  // the synthesized click that follows pointerup. We use a ref + state on the
  // wrapper because pointerup runs before click.
  const justDraggedRef = React.useRef(false);
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    justDraggedRef.current = dragRef.current?.moved ?? false;
    endDrag(e);
  };
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      e.preventDefault();
      return;
    }
    handleClick();
  };

  // Chevron direction:
  // - Side panel on left, expanded: points left (click to collapse leftward)
  // - Side panel on left, collapsed: points right (click to expand rightward)
  // - Side panel on right, expanded: points right
  // - Side panel on right, collapsed: points left
  const pointsLeft = sidePanelOnLeft ? !collapsed : collapsed;
  const Chevron = pointsLeft ? ChevronLeft : ChevronRight;

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t.resize.resize(t.views[side])}
      data-testid={`resize-handle-${side}`}
      className={cn(
        'group relative flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-border/40 hover:bg-border'
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={endDrag}
    >
      <button
        type="button"
        aria-label={collapsed ? t.resize.expand(t.views[side]) : t.resize.collapse(t.views[side])}
        data-testid={`collapse-toggle-${side}`}
        onClick={handleButtonClick}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          'absolute z-10 flex h-6 w-4 cursor-pointer items-center justify-center rounded-sm border border-border bg-background text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100',
          collapsed && 'opacity-100'
        )}
      >
        <Chevron className="h-3 w-3" />
      </button>
    </div>
  );
}
